var _dec, _dec2, _dec3, _class, _Options;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component, Children } from 'react';
import { omitProps, matchComponentTypes, callRenderProp, safeCloneElement, withDeterministicId } from '@instructure/ui-react-utils';
import { testable } from '@instructure/ui-testable';
import { View } from '@instructure/ui-view';
import { withStyle } from '@instructure/emotion';
import generateStyles from './styles';
import generateComponentTheme from './theme';
import { Item } from './Item';
import { Separator } from './Separator';
import { allowedProps, propTypes } from './props';
import { isAndroidOrIOS } from '@instructure/ui-utils';
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
---
category: components
---
**/
let Options = (_dec = withDeterministicId(), _dec2 = withStyle(generateStyles, generateComponentTheme), _dec3 = testable(), _dec(_class = _dec2(_class = _dec3(_class = (_Options = class Options extends Component {
  constructor(...args) {
    super(...args);
    this.ref = null;
    this.handleRef = el => {
      this.ref = el;
    };
    this._labelId = this.props.deterministicId('Options-label');
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props);
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props2;
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2);
  }
  get childAs() {
    const as = this.props.as;
    if (as === 'ul' || as === 'ol') {
      return 'li';
    }
    return void 0;
  }
  renderLabel() {
    const _this$props3 = this.props,
      renderLabel = _this$props3.renderLabel,
      styles = _this$props3.styles;
    return _jsx("span", {
      id: this._labelId,
      role: "presentation"
      // because TalkBack and iOS VoiceOver don't announce sub-menu labels, aria-hidden needs to be false to achive that
      ,
      "aria-hidden": isAndroidOrIOS() ? 'false' : 'true',
      css: styles === null || styles === void 0 ? void 0 : styles.label,
      children: callRenderProp(renderLabel)
    });
  }
  renderSubList(subOptions) {
    const styles = this.props.styles;
    return _jsx(Item, {
      as: this.childAs,
      role: "presentation",
      css: styles === null || styles === void 0 ? void 0 : styles.label,
      children: subOptions
    });
  }
  renderChildren() {
    const children = this.props.children;
    return Children.map(children, child => {
      if (matchComponentTypes(child, ['Options'])) {
        return this.renderSubList(child);
      }
      if (matchComponentTypes(child, ['Item', 'Separator'])) {
        return safeCloneElement(child, {
          as: this.childAs || child.props.as
        });
      }
      return void 0;
    });
  }
  get ariaLabelledby() {
    if (this.props.renderLabel) {
      return this._labelId;
    }
    if (this.props['aria-labelledby']) {
      return this.props['aria-labelledby'];
    }
    return void 0;
  }
  render() {
    const passthroughProps = View.omitViewProps(omitProps(this.props, Options.allowedProps), Options);
    const _this$props4 = this.props,
      as = _this$props4.as,
      role = _this$props4.role,
      renderLabel = _this$props4.renderLabel,
      elementRef = _this$props4.elementRef,
      styles = _this$props4.styles;
    return _jsxs("div", {
      css: styles === null || styles === void 0 ? void 0 : styles.options,
      role: "presentation",
      ref: this.handleRef,
      children: [renderLabel && this.renderLabel(), _jsx(View, {
        ...passthroughProps,
        elementRef: elementRef,
        css: styles === null || styles === void 0 ? void 0 : styles.list,
        as: as,
        role: role,
        display: "block",
        margin: "none",
        padding: "none",
        background: "primary",
        "aria-labelledby": this.ariaLabelledby,
        children: this.renderChildren()
      })]
    });
  }
}, _Options.displayName = "Options", _Options.componentId = 'Options', _Options.allowedProps = allowedProps, _Options.propTypes = propTypes, _Options.defaultProps = {
  as: 'span',
  role: 'list',
  elementRef: () => {},
  renderLabel: null,
  children: null
}, _Options.Item = Item, _Options.Separator = Separator, _Options)) || _class) || _class) || _class);
export default Options;
export { Options };